export type LineChartProps = {
  title: string;
  data: number[];
  xLabels: string[];
  yMax?: number;
  /** Accessible label override; auto-generated from data when omitted. */
  ariaLabel?: string;
};

const VIEW_W = 600;
const VIEW_H = 240;
const PAD_LEFT = 40;
const PAD_RIGHT = 12;
const PAD_TOP = 24;
const PAD_BOTTOM = 36;
const PLOT_W = VIEW_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = VIEW_H - PAD_TOP - PAD_BOTTOM;

function buildPoints(data: number[], yMax: number): Array<{ x: number; y: number; value: number }> {
  if (data.length === 0) return [];
  const stepX = data.length === 1 ? 0 : PLOT_W / (data.length - 1);
  return data.map((value, idx) => {
    const clamped = Math.max(0, Math.min(value, yMax));
    const x = PAD_LEFT + idx * stepX;
    const y = PAD_TOP + (1 - clamped / yMax) * PLOT_H;
    return { x, y, value };
  });
}

function describeLine(points: Array<{ x: number; y: number }>): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
}

function describeArea(points: Array<{ x: number; y: number }>): string {
  if (points.length === 0) return '';
  const first = points[0];
  const last = points[points.length - 1];
  const baseline = PAD_TOP + PLOT_H;
  return [
    `M ${first.x.toFixed(2)} ${baseline.toFixed(2)}`,
    ...points.map((p) => `L ${p.x.toFixed(2)} ${p.y.toFixed(2)}`),
    `L ${last.x.toFixed(2)} ${baseline.toFixed(2)}`,
    'Z'
  ].join(' ');
}

function defaultAriaLabel(title: string, xLabels: string[], data: number[]): string {
  return `${title}：${xLabels.map((label, i) => `${label} ${data[i] ?? 0}`).join('，')}`;
}

function LineChart({
  title,
  data,
  xLabels,
  yMax = 250,
  ariaLabel
}: LineChartProps) {
  const points = buildPoints(data, yMax);
  const linePath = describeLine(points);
  const areaPath = describeArea(points);
  const yTicks = [0, 50, 100, 150, 200, 250].filter((v) => v <= yMax);
  const finalAria = ariaLabel ?? defaultAriaLabel(title, xLabels, data);

  return (
    <article className="axi-chart-card" role="img" aria-label={finalAria}>
      <h3 className="axi-chart-card__title">{title}</h3>
      <svg
        className="axi-chart-card__svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="line-chart-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {yTicks.map((tick) => {
          const y = PAD_TOP + (1 - tick / yMax) * PLOT_H;
          return (
            <g key={`y-${tick}`}>
              <line
                x1={PAD_LEFT}
                x2={PAD_LEFT + PLOT_W}
                y1={y}
                y2={y}
                stroke="var(--primary)"
                strokeOpacity="0.055"
                strokeWidth="0.75"
              />
              <text
                x={PAD_LEFT - 8}
                y={y + 3}
                fill="var(--text-muted)"
                fontSize="10"
                textAnchor="end"
              >
                {tick}
              </text>
            </g>
          );
        })}

        {areaPath ? <path d={areaPath} fill="url(#line-chart-area)" /> : null}
        <path
          d={linePath}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {points.map((p, i) => (
          <circle
            key={`pt-${i}`}
            cx={p.x}
            cy={p.y}
            r="3"
            fill="var(--primary)"
            stroke="var(--bg-page)"
            strokeWidth="2.25"
          />
        ))}

        {xLabels.map((label, i) => {
          const stepX = points.length === 1 ? 0 : PLOT_W / (points.length - 1);
          const x = PAD_LEFT + i * stepX;
          return (
            <text
              key={`x-${label}-${i}`}
              x={x}
              y={VIEW_H - 12}
              fill="var(--text-muted)"
              fontSize="10"
              textAnchor="middle"
            >
              {label}
            </text>
          );
        })}
      </svg>
    </article>
  );
}

export default LineChart;